// Accounting utilities for frontend validation and error handling
// These are preventive validations before sending to backend

export interface JournalEntryLine {
  accountCode?: string;
  description?: string;
  debit?: number;
  credit?: number;
}

export interface JournalEntryFormData {
  description: string;
  journalId: string;
  periodId: string;
  lines: JournalEntryLine[];
}

// Preventive validation error codes
export const VALIDATION_ERRORS = {
  MINIMUM_LINES: 'MINIMUM_LINES',
  UNBALANCED: 'UNBALANCED',
  FUTURE_DATE: 'FUTURE_DATE',
  NEGATIVE_AMOUNT: 'NEGATIVE_AMOUNT',
  DUPLICATE_ACCOUNT: 'DUPLICATE_ACCOUNT',
  DEBIT_CREDIT_CONFLICT: 'DEBIT_CREDIT_CONFLICT',
  INVALID_AMOUNT: 'INVALID_AMOUNT',
} as const;

// Backend error code to user-friendly message mapping
export const BACKEND_ERROR_MESSAGES: Record<string, string> = {
  ERR_UNBALANCED_JOURNAL: 'El asiento no está balanceado. Los débitos deben ser iguales a los créditos.',
  ERR_MINIMUM_LINES: 'El asiento debe tener al menos 2 líneas (1 débito y 1 crédito).',
  ERR_DUPLICATE_ACCOUNT: 'No se puede usar la misma cuenta dos veces en un asiento.',
  ERR_PERIOD_CLOSED: 'El período está cerrado. No se pueden crear nuevos asientos.',
  ERR_FUTURE_DATE: 'La fecha del asiento no puede ser en el futuro.',
  ERR_HEADING_ACCOUNT: 'No se puede usar una cuenta de encabezado en asientos.',
  ERR_ARCHIVED_ACCOUNT: 'La cuenta está archivada y no se puede usar.',
  ERR_ACCOUNT_NOT_FOUND: 'La cuenta especificada no existe.',
  ERR_ACCOUNT_COMPANY_MISMATCH: 'La cuenta no pertenece a la empresa seleccionada.',
  ERR_JOURNAL_NOT_FOUND: 'El diario especificado no existe.',
  ERR_JOURNAL_COMPANY_MISMATCH: 'El diario no pertenece a la empresa seleccionada.',
  ERR_NO_OPEN_PERIOD: 'No hay un período abierto para esta fecha.',
  ERR_DATE_OUT_OF_RANGE: 'La fecha está fuera del rango del período seleccionado.',
  ERR_TRANSACTION_FAILED: 'Error en la transacción. Por favor intenta de nuevo.',
};

// Preventive validation: minimum 2 lines
export function validateMinimumLines(lines: JournalEntryLine[]): { valid: boolean; error?: string } {
  if (!lines || lines.length < 2) {
    return { valid: false, error: 'Se requieren al menos 2 líneas' };
  }
  return { valid: true };
}

// Preventive validation: debits = credits
export function validateBalance(lines: JournalEntryLine[]): { valid: boolean; error?: string; totalDebits?: number; totalCredits?: number } {
  const totalDebits = lines.reduce((sum, line) => sum + (line.debit || 0), 0);
  const totalCredits = lines.reduce((sum, line) => sum + (line.credit || 0), 0);

  // Handle floating point precision (allow 0.01 difference)
  const difference = Math.abs(totalDebits - totalCredits);
  if (difference > 0.01) {
    return {
      valid: false,
      error: `Débitos (${totalDebits.toFixed(2)}) != Créditos (${totalCredits.toFixed(2)})`,
      totalDebits,
      totalCredits,
    };
  }

  return { valid: true, totalDebits, totalCredits };
}

// Preventive validation: no negative amounts
export function validatePositiveAmounts(lines: JournalEntryLine[]): { valid: boolean; error?: string } {
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if ((line.debit && line.debit < 0) || (line.credit && line.credit < 0)) {
      return { valid: false, error: `Línea ${i + 1}: Los montos no pueden ser negativos` };
    }
  }
  return { valid: true };
}

// Preventive validation: debit and credit exclusivity (no both non-zero)
export function validateDebitCreditExclusivity(lines: JournalEntryLine[]): { valid: boolean; error?: string } {
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const hasDebit = line.debit && line.debit > 0;
    const hasCredit = line.credit && line.credit > 0;

    if (hasDebit && hasCredit) {
      return { valid: false, error: `Línea ${i + 1}: No puede tener débito y crédito simultáneamente` };
    }

    if (!hasDebit && !hasCredit) {
      return { valid: false, error: `Línea ${i + 1}: Debe tener débito o crédito` };
    }
  }
  return { valid: true };
}

// Preventive validation: no duplicate accounts
export function validateNoDuplicateAccounts(lines: JournalEntryLine[]): { valid: boolean; error?: string } {
  const accountCodes = lines
    .map(line => line.accountCode)
    .filter((code): code is string => !!code);

  const uniqueAccounts = new Set(accountCodes);
  if (uniqueAccounts.size !== accountCodes.length) {
    return { valid: false, error: 'No se puede usar la misma cuenta dos veces' };
  }
  return { valid: true };
}

// Run all preventive validations
export function validateEntryLocally(lines: JournalEntryLine[]): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  const minCheck = validateMinimumLines(lines);
  if (!minCheck.valid) errors.push(minCheck.error!);

  const amountsCheck = validatePositiveAmounts(lines);
  if (!amountsCheck.valid) errors.push(amountsCheck.error!);

  const exclusivityCheck = validateDebitCreditExclusivity(lines);
  if (!exclusivityCheck.valid) errors.push(exclusivityCheck.error!);

  const balanceCheck = validateBalance(lines);
  if (!balanceCheck.valid) errors.push(balanceCheck.error!);

  const dupsCheck = validateNoDuplicateAccounts(lines);
  if (!dupsCheck.valid) errors.push(dupsCheck.error!);

  return {
    valid: errors.length === 0,
    errors,
  };
}

// Format error message from backend
export function formatBackendError(errorCode: string, details?: any): string {
  const message = BACKEND_ERROR_MESSAGES[errorCode];
  if (!message) {
    return details?.message || 'Error desconocido. Por favor intenta de nuevo.';
  }
  return message;
}

// Check if error is an accounting error
export function isAccountingError(error: any): error is { code: string; message: string; details: any } {
  return error && typeof error === 'object' && 'code' in error && 'message' in error;
}
